import { HomeOutlined, UserOutlined } from '@ant-design/icons';
import { Button, Flex } from '@chakra-ui/react';
import { isSlideAnamneseSlide, Slide } from '@llx/common';
import { useEffect, useRef, useState } from 'react';

import { IsMobile } from '@/utils/helpers/browser.helper';
import useOnScreen from '@/utils/hooks/useOnScreen';

type Props = {
  currentSlideIdx: number;
  slides: Slide[];

  onSlideChange: (slideIndex: number) => void;
  onHomeClick: () => void;
  showHomeButton?: boolean;
  deactivatedSlideIds?: string[];
  isHomeBtnDisabled?: boolean;
  isPatientCaseBtnDisabled?: boolean;
};

function CaseSlideNavigator({
  currentSlideIdx,
  slides,
  onSlideChange,
  onHomeClick,
  showHomeButton,
  deactivatedSlideIds,
  isHomeBtnDisabled,
  isPatientCaseBtnDisabled,
}: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const firstSlideBtnRef = useRef<HTMLButtonElement>(null);
  const lastSlideBtnRef = useRef<HTMLButtonElement>(null);
  const isFirstSlideBtnVisible = useOnScreen(firstSlideBtnRef);
  const isLastSlideBtnVisible = useOnScreen(lastSlideBtnRef);

  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [hasDragged, setHasDragged] = useState(false);

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!containerRef.current) return;
    setIsDragging(true);
    setHasDragged(false); // Reset
    setStartX(e.pageX - containerRef.current.offsetLeft);
    setScrollLeft(containerRef.current.scrollLeft);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !containerRef.current) return;
    const x = e.pageX - containerRef.current.offsetLeft;
    const walk = x - startX;
    if (Math.abs(walk) > 5) {
      setHasDragged(true); // Mark as dragged if moved more than 5px
    }
    containerRef.current.scrollLeft = scrollLeft - walk;
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseLeave = () => {
    setIsDragging(false);
  };

  const handleButtonClick = (slide: number) => {
    if (slide === -1) {
      onHomeClick();
    } else {
      onSlideChange(slide);
    }
  };

  const hasPatientCase = !!slides.find(isSlideAnamneseSlide);

  useEffect(() => {
    if (containerRef.current) {
      //NOTE: Button width + the gap between buttons
      const buttonWidth = 50 + 8;

      const containerWidth = containerRef.current.offsetWidth;
      const offset = containerWidth / 2 - buttonWidth / 2;
      const target = currentSlideIdx * buttonWidth - offset;

      containerRef.current.scrollTo({
        left: target,
        behavior: 'smooth',
      });
    }
  }, [currentSlideIdx]);

  return (
    <Flex
      /* NOTE: we have to set the width here, it does not matter to what, but it's needed for the
      live actions button and refresh button to be able to be shown */
      w="100%"
      alignItems="center"
      overflow="auto"
      flexGrow={1}
      flexShrink={1}
      px={2}
      css={{
        '&::-webkit-scrollbar': {
          display: 'none',
        },
        scrollbarWidth: 'none', // Firefox
        msOverflowStyle: 'none', // IE and Edge
      }}
    >
      <Flex gap="8px">
        {showHomeButton && (
          <Button
            variant="slideNavigator"
            onClick={() => {
              handleButtonClick(-1);
            }}
            isDisabled={isHomeBtnDisabled}
          >
            <HomeOutlined />
          </Button>
        )}
        {hasPatientCase && (
          <Button
            variant={currentSlideIdx === 0 ? 'slideNavigatorSelected' : 'slideNavigator'}
            flex="1 1 auto"
            data-test="nav_item_0"
            onClick={(e) => {
              if (hasDragged) {
                e.preventDefault();
                e.stopPropagation();
                return;
              }
              handleButtonClick(0);
            }}
            minWidth="50px"
            isDisabled={isPatientCaseBtnDisabled}
          >
            <UserOutlined />
          </Button>
        )}
      </Flex>
      <Flex position="relative" overflowX="hidden" flexGrow={1}>
        <Flex
          ref={containerRef}
          padding="10px"
          alignItems="center"
          gap="8px"
          overflowX="auto"
          overflowY="hidden"
          data-test="slide-navigator"
          w="full"
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseLeave}
          cursor={isDragging ? 'grabbing' : 'grab'}
          sx={{
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              bottom: 0,
              width: 10,
              zIndex: 1,
              backgroundImage: `linear-gradient(to right, rgba(214, 218, 255, 1) 0%, rgba(214, 218, 255, 0) 100%)`,
              left: 0,
              pointerEvents: 'none',
              opacity: isFirstSlideBtnVisible ? 0 : 1,
            },
            '&::after': {
              content: '""',
              position: 'absolute',
              top: 0,
              bottom: 0,
              right: 0,
              width: 10,
              zIndex: 1,
              backgroundImage: `linear-gradient(to left, rgba(214, 218, 255, 1) 0%, rgba(214, 218, 255, 0) 100%)`,
              pointerEvents: 'none',
              opacity: isLastSlideBtnVisible ? 0 : 1,
            },
          }}
          css={{
            '&::-webkit-scrollbar': {
              display: 'none',
            },
            scrollbarWidth: 'none', // Firefox
            msOverflowStyle: 'none', // IE and Edge
          }}
        >
          {slides.map((slide, index) => {
            const buttonName = `button_${index}`;
            if (hasPatientCase && index === 0) return null;
            let ref = null;
            if (index === (hasPatientCase ? 1 : 0)) {
              ref = firstSlideBtnRef;
            } else if (index === slides.length - 1) {
              ref = lastSlideBtnRef;
            }
            const isDeactivated = deactivatedSlideIds?.includes(slide.id);

            return (
              <Button
                ref={ref}
                key={`${buttonName}_${index === currentSlideIdx ? 'selected' : ''}`}
                variant={index === currentSlideIdx ? 'slideNavigatorSelected' : 'slideNavigator'}
                flex="1 1 auto"
                data-test={`nav_item_${index}`}
                onClick={(e) => {
                  if (hasDragged) {
                    e.preventDefault();
                    e.stopPropagation();
                    return;
                  }
                  handleButtonClick(index);
                }}
                minWidth="50px"
                isDisabled={isDeactivated}
                {...(IsMobile() && {
                  _hover: {},
                })}
              >
                {hasPatientCase && index !== 0 && index}
                {!hasPatientCase && index + 1}
              </Button>
            );
          })}
        </Flex>
      </Flex>
    </Flex>
  );
}

export default CaseSlideNavigator;
